// Capped cutaways: clip planes that cut a model open, and caps that close the
// cut parts so they read as solids (ADR 0004, spikes/cutaway/REPORT.md).
//
// The view state gives up to six planes {normal, offset}; a point p with
// normal · p > offset lies beyond a plane, and a point beyond every plane is
// cut away. One plane cuts the model in half; three planes remove a corner.
//
// Every cut group (psim.cutGroup) gets its own clones of the materials it uses
// and its own cap colour, so a lesson can leave one group whole or hide it,
// and a cut face says which group it belongs to. Caps are drawn through the
// stencil buffer, as in three.js's webgl_clipping_stencil example, for each
// part and plane: the part, clipped by that plane alone, adds its back faces
// and subtracts its front faces in the stencil, and a quad on the plane,
// clipped to where the other planes cut, is drawn where the count is not
// zero. That is correct because parts are closed and do not intersect (the
// part contract), so the count is not zero exactly inside the part. The
// spike's two savings are kept: a part and plane pair is skipped when the
// part's bounding sphere cannot reach that cap, and the stencil is reset by
// drawing the quad again rather than clearing the buffer. A cap is flat, so it
// is drawn unlit in a colour shaded once for its plane's facing.
//
// Without a stencil buffer the cutaway falls back to the spike's back faces:
// each part's inside drawn flat in its cap colour. It looks close from most
// angles but is not on the plane, so it is the fallback, not the default.
//
// pick() casts a ray that ignores the cut-away geometry and reports a cap when
// the ray enters a part through one.

import {
  AlwaysStencilFunc,
  BackSide,
  Color,
  DecrementWrapStencilOp,
  DoubleSide,
  FrontSide,
  IncrementWrapStencilOp,
  Material,
  Mesh,
  MeshBasicMaterial,
  NotEqualStencilFunc,
  Object3D,
  Plane,
  PlaneGeometry,
  Ray,
  ReplaceStencilOp,
  Sphere,
  Triangle,
  Vector3,
  type BufferGeometry,
  type Intersection,
  type Raycaster,
  type Scene,
  type Side,
  type StencilOp,
} from "three";

export type CapMode = "stencil" | "backface" | "none";

export interface CutPlaneInput {
  normal: readonly [number, number, number] | readonly number[];
  offset: number;
}

/** A part node of the model: the object carrying psim.part, and its meshes. */
export interface ScenePart {
  readonly object: Object3D;
  readonly id: string;
  readonly cutGroup: string;
  readonly meshes: readonly Mesh[];
}

export interface PickResult {
  part: ScenePart;
  /** World point, metres. */
  point: Vector3;
  distance: number;
  /** True when the ray hit the cap of a cut part rather than its surface. */
  onCap: boolean;
}

export const MAX_PLANES = 6;
/**
 * How far, in metres, a cap reaches past the other planes. Where two caps meet
 * (the edge of a corner cut) each is clipped by the other's plane, and without
 * an overlap single pixels on that edge belong to neither. The overlap lies
 * inside the uncut solid, so the part's own surface hides it.
 */
const CAP_OVERLAP = 1e-4;
/** Points this close to a plane, in metres, count as on it: the cap a ray leaves the cut on. */
const ON_PLANE = 1e-9;
/** The render order of the parts themselves: after every stencil pass and cap. */
const PART_ORDER = 1_000_000;
/**
 * Cap colours, one per cut group in the order the model first names them, so a
 * cut face says which group it belongs to (like section hatching by part).
 */
export const CAP_COLORS = [0xc9634a, 0x5b8fc4, 0xd8a64a, 0x6aa86e, 0x9c80c9, 0x5fb3b3, 0xc97aa0, 0x9aa35a];

interface CapPass {
  readonly group: CutGroup;
  readonly part: ScenePart;
  readonly plane: number;
  readonly stencil: Mesh[];
  readonly cap: Mesh;
  readonly reset: Mesh;
}

interface CutGroup {
  readonly id: string;
  readonly parts: ScenePart[];
  /** Clones of the materials its parts use, by the original's uuid. */
  readonly materials: Map<string, Material>;
  readonly capColor: Color;
  /** The group's cap material for each plane, coloured for that plane's facing. */
  readonly capMaterials: MeshBasicMaterial[];
  /** Back-face fallback meshes, one per part mesh. */
  readonly backfaces: Mesh[];
  visible: boolean;
  cut: boolean;
}

/** Finds the part nodes under `root`: objects whose userData has psim.part. */
export function findParts(root: Object3D): ScenePart[] {
  const parts: ScenePart[] = [];
  root.traverse((object) => {
    const id = object.userData["psim.part"];
    if (typeof id !== "string") return;
    const meshes: Mesh[] = [];
    object.traverse((o) => {
      if ((o as Mesh).isMesh) meshes.push(o as Mesh);
    });
    const cutGroup = object.userData["psim.cutGroup"];
    parts.push({ object, id, cutGroup: typeof cutGroup === "string" ? cutGroup : "default", meshes });
  });
  return parts;
}

function stencilMaterial(side: Side, op: StencilOp, plane: Plane): MeshBasicMaterial {
  return new MeshBasicMaterial({
    side,
    clippingPlanes: [plane],
    depthWrite: false,
    depthTest: false,
    colorWrite: false,
    stencilWrite: true,
    stencilFunc: AlwaysStencilFunc,
    stencilFail: op,
    stencilZFail: op,
    stencilZPass: op,
  });
}

const _sphere = new Sphere();
const _target = new Vector3();
const _a = new Vector3();
const _b = new Vector3();
const _c = new Vector3();
const _triangle = new Triangle();
const _normal = new Vector3();

export class Cutaway {
  readonly parts: readonly ScenePart[];
  readonly mode: CapMode;
  /** The planes as three.js clips them: a fragment is cut when it lies behind every plane. */
  private readonly clip: Plane[] = [];
  /** The same planes facing the other way: the half-spaces that are cut. */
  private readonly beyond: Plane[] = [];
  /** The cut half-spaces widened by CAP_OVERLAP, which the caps are clipped to. */
  private readonly widened: Plane[] = [];
  /** For each plane, the other planes' widened half-spaces: where its cap may be drawn. */
  private readonly capClip: Plane[][] = [];
  private readonly groups = new Map<string, CutGroup>();
  private readonly passes: CapPass[] = [];
  private readonly scene: Scene;
  private readonly capGeometry = new PlaneGeometry(1, 1);
  private readonly disposables: { dispose(): void }[] = [this.capGeometry];
  private planeCount = 0;
  private readonly shade: (normal: Vector3) => number;
  private readonly spheres = new Map<ScenePart, Sphere>();

  /**
   * @param shade the brightness of a cap facing along a unit normal, for the
   *   scene's lights. A cap is flat, so its lighting is one number per plane:
   *   the caps are drawn unlit in their shaded colour, which keeps their shader
   *   cheap (they cover a lot of screen).
   */
  constructor(scene: Scene, parts: readonly ScenePart[], mode: CapMode, shade: (normal: Vector3) => number = () => 1) {
    this.scene = scene;
    this.shade = shade;
    this.parts = parts;
    this.mode = mode;
    for (let i = 0; i < MAX_PLANES; i++) {
      this.clip.push(new Plane(new Vector3(-1, 0, 0), 0));
      this.beyond.push(new Plane(new Vector3(1, 0, 0), 0));
      this.widened.push(new Plane(new Vector3(1, 0, 0), 0));
    }
    for (let i = 0; i < MAX_PLANES; i++) this.capClip.push([]);

    for (const part of parts) {
      let group = this.groups.get(part.cutGroup);
      if (!group) {
        group = {
          id: part.cutGroup,
          parts: [],
          materials: new Map(),
          capColor: new Color(CAP_COLORS[this.groups.size % CAP_COLORS.length]),
          capMaterials: [],
          backfaces: [],
          visible: true,
          cut: true,
        };
        this.groups.set(part.cutGroup, group);
      }
      group.parts.push(part);
      for (const mesh of part.meshes) {
        mesh.geometry.computeBoundingSphere();
        mesh.material = this.groupMaterial(group, mesh.material as Material);
        mesh.renderOrder = PART_ORDER;
      }
    }
    if (mode === "backface") this.buildBackfaces();
    if (mode === "stencil") this.buildStencilPasses();
    this.applyPlanes();
  }

  /** The cut groups, in the order their first part appears. */
  get groupIds(): string[] {
    return [...this.groups.keys()];
  }

  /** How many cap passes would draw on the next frame (after update()). */
  get activeCaps(): number {
    return this.passes.filter((p) => p.cap.visible).length;
  }

  /** Sets the planes (at most six; more are ignored). A zero normal is skipped. */
  setPlanes(planes: readonly CutPlaneInput[]): void {
    let n = 0;
    for (const p of planes) {
      if (n === MAX_PLANES) break;
      const normal = new Vector3(p.normal[0] ?? 0, p.normal[1] ?? 0, p.normal[2] ?? 0);
      const length = normal.length();
      if (!(length > 0) || !Number.isFinite(p.offset)) continue;
      normal.divideScalar(length);
      const offset = p.offset / length;
      // Cut where normal · p > offset: three.js clips where its plane's distance is negative.
      this.clip[n]!.set(normal.clone().negate(), offset);
      this.beyond[n]!.set(normal, -offset);
      this.widened[n]!.set(normal, CAP_OVERLAP - offset);
      n++;
    }
    if (n !== this.planeCount) {
      this.planeCount = n;
      this.applyPlanes();
    }
    for (const g of this.groups.values()) {
      g.capMaterials.forEach((m, i) => {
        if (i < n) m.color.copy(g.capColor).multiplyScalar(this.shade(this.beyond[i]!.normal));
      });
    }
  }

  /** The number of active planes. */
  get planes(): number {
    return this.planeCount;
  }

  /** Shows or hides a cut group: its parts and its caps. */
  setGroupVisible(group: string, visible: boolean): void {
    const g = this.groups.get(group);
    if (!g) return;
    g.visible = visible;
    for (const part of g.parts) part.object.visible = visible;
  }

  /** Whether a cut group is cut by the planes (true) or left whole (false). */
  setGroupCut(group: string, cut: boolean): void {
    const g = this.groups.get(group);
    if (!g || g.cut === cut) return;
    g.cut = cut;
    this.applyPlanes();
  }

  /** Whether the world point p is cut away. A point on a plane (within 1 nm) is not. */
  isCut(p: Vector3): boolean {
    if (this.planeCount === 0) return false;
    for (let i = 0; i < this.planeCount; i++) if (this.beyond[i]!.distanceToPoint(p) <= ON_PLANE) return false;
    return true;
  }

  /** Places the caps for the parts' current world matrices. Call after the scene's matrices are updated. */
  update(): void {
    if (this.mode !== "stencil") return;
    for (const g of this.groups.values()) {
      if (!g.visible || !g.cut || this.planeCount === 0) continue;
      for (const part of g.parts) partSphere(part, this.spheres);
    }
    for (const pass of this.passes) {
      const g = pass.group;
      const i = pass.plane;
      let active = g.visible && g.cut && i < this.planeCount;
      if (active) {
        const sphere = this.spheres.get(pass.part)!;
        const r = sphere.radius;
        const c = sphere.center;
        const s = this.beyond[i]!.distanceToPoint(c);
        // The cap lies on plane i where every other plane cuts; skip the pair when
        // the part misses plane i or lies wholly in front of another plane.
        active = Math.abs(s) < r;
        for (let j = 0; active && j < this.planeCount; j++) {
          if (j !== i && this.beyond[j]!.distanceToPoint(c) < -r) active = false;
        }
        if (active) {
          const plane = this.beyond[i]!;
          plane.projectPoint(c, pass.cap.position);
          pass.cap.lookAt(_target.copy(pass.cap.position).add(plane.normal));
          pass.cap.scale.setScalar(2.02 * r);
          pass.cap.updateMatrixWorld();
        }
      }
      pass.cap.visible = active;
      for (const s of pass.stencil) s.visible = active;
    }
  }

  /**
   * The first part the ray meets outside the cut-away region, or null. A ray
   * that enters a cut part through its cut face reports that face (onCap).
   */
  pick(raycaster: Raycaster): PickResult | null {
    const meshes: Mesh[] = [];
    const owner = new Map<Mesh, ScenePart>();
    for (const part of this.parts) {
      if (!visibleInScene(part.object)) continue;
      for (const m of part.meshes) {
        meshes.push(m);
        owner.set(m, part);
      }
    }
    // Back faces count too: hitting one first means the ray came in through a cap.
    const sides = new Map<Material, Side>();
    for (const m of meshes) {
      const mat = m.material as Material;
      if (!sides.has(mat)) sides.set(mat, mat.side);
      mat.side = DoubleSide;
    }
    let hits: Intersection[];
    try {
      hits = raycaster.intersectObjects(meshes, false);
    } finally {
      for (const [mat, side] of sides) mat.side = side;
    }
    const ray = raycaster.ray;
    for (const hit of hits) {
      const mesh = hit.object as Mesh;
      const part = owner.get(mesh);
      if (!part) continue;
      const group = this.groups.get(part.cutGroup);
      const cut = group?.cut ?? true;
      if (cut && this.isCut(hit.point)) continue;
      if (cut && hit.face && isBackFace(mesh, hit.face, ray)) {
        const exit = this.exitDistance(ray);
        if (exit !== null && exit <= hit.distance) {
          return { part, point: ray.at(exit, new Vector3()), distance: exit, onCap: true };
        }
      }
      return { part, point: hit.point.clone(), distance: hit.distance, onCap: false };
    }
    return null;
  }

  dispose(): void {
    for (const pass of this.passes) pass.cap.removeFromParent();
    for (const d of this.disposables) d.dispose();
  }

  // ---- building ----------------------------------------------------------

  private groupMaterial(group: CutGroup, original: Material): Material {
    let clone = group.materials.get(original.uuid);
    if (!clone) {
      clone = original.clone();
      clone.side = FrontSide;
      clone.clipShadows = true;
      group.materials.set(original.uuid, clone);
      this.disposables.push(clone);
    }
    return clone;
  }

  private buildBackfaces(): void {
    for (const g of this.groups.values()) {
      const material = new MeshBasicMaterial({ color: g.capColor, side: BackSide, clipIntersection: true });
      this.disposables.push(material);
      g.materials.set(`backface:${g.id}`, material);
      for (const part of g.parts) {
        for (const mesh of part.meshes) {
          const back = new Mesh(mesh.geometry, material);
          back.renderOrder = PART_ORDER;
          back.raycast = () => {};
          back.userData["psim.helper"] = "backface";
          mesh.add(back);
          g.backfaces.push(back);
        }
      }
    }
  }

  private buildStencilPasses(): void {
    const reset = new MeshBasicMaterial({
      side: DoubleSide,
      depthWrite: false,
      depthTest: false,
      colorWrite: false,
      stencilWrite: true,
      stencilRef: 0,
      stencilFunc: AlwaysStencilFunc,
      stencilFail: ReplaceStencilOp,
      stencilZFail: ReplaceStencilOp,
      stencilZPass: ReplaceStencilOp,
    });
    this.disposables.push(reset);
    // Each plane's stencil passes clip the parts by that plane alone.
    const back = this.clip.map((p) => stencilMaterial(BackSide, IncrementWrapStencilOp, p));
    const front = this.clip.map((p) => stencilMaterial(FrontSide, DecrementWrapStencilOp, p));
    this.disposables.push(...back, ...front);
    for (const g of this.groups.values()) {
      for (let i = 0; i < MAX_PLANES; i++) {
        const material = new MeshBasicMaterial({
          color: g.capColor,
          side: DoubleSide,
          clippingPlanes: this.capClip[i]!,
          // A part built of overlapping shells has inner faces that meet the cut edge
          // on; pulled a hair towards the eye, the cap wins those depth ties.
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -1,
          stencilWrite: true,
          stencilRef: 0,
          stencilFunc: NotEqualStencilFunc,
          stencilFail: ReplaceStencilOp,
          stencilZFail: ReplaceStencilOp,
          stencilZPass: ReplaceStencilOp,
        });
        g.capMaterials.push(material);
        this.disposables.push(material);
      }
    }
    // One pass per part and plane: the part's stencil count, its cap, the reset.
    // Per part rather than per group, so each cap quad covers one part's
    // cross-section and culls on its own.
    let order = 1;
    for (const g of this.groups.values()) {
      for (const part of g.parts) {
        this.spheres.set(part, new Sphere());
        for (let i = 0; i < MAX_PLANES; i++) {
          const stencil: Mesh[] = [];
          for (const mesh of part.meshes) {
            for (const material of [back[i]!, front[i]!]) {
              const s = new Mesh(mesh.geometry, material);
              s.renderOrder = order;
              s.raycast = () => {};
              s.visible = false;
              s.userData["psim.helper"] = "stencil";
              mesh.add(s);
              stencil.push(s);
            }
          }
          const cap = new Mesh(this.capGeometry, g.capMaterials[i]!);
          cap.renderOrder = order + 1;
          cap.raycast = () => {};
          cap.userData["psim.helper"] = "cap";
          cap.userData["psim.part"] = part.id;
          cap.visible = false;
          // The stencil counts the other planes clip away stay behind; the cap quad
          // covers the part's whole cross-section, so drawing it again resets them.
          const resetMesh = new Mesh(this.capGeometry, reset);
          resetMesh.renderOrder = order + 2;
          resetMesh.raycast = () => {};
          cap.add(resetMesh);
          this.scene.add(cap);
          this.passes.push({ group: g, part, plane: i, stencil, cap, reset: resetMesh });
          order += 3;
        }
      }
    }
  }

  /** Points the materials at the active planes; a change of count recompiles their shaders. */
  private applyPlanes(): void {
    const active = this.clip.slice(0, this.planeCount);
    for (let i = 0; i < MAX_PLANES; i++) {
      const others = this.capClip[i]!;
      others.length = 0;
      for (let j = 0; j < this.planeCount; j++) if (j !== i) others.push(this.widened[j]!);
    }
    for (const g of this.groups.values()) {
      const planes = g.cut ? active : [];
      for (const m of g.materials.values()) {
        m.clippingPlanes = planes;
        m.clipIntersection = true;
        m.needsUpdate = true;
      }
      for (const b of g.backfaces) b.visible = g.cut && this.planeCount > 0;
    }
    for (const g of this.groups.values()) {
      g.capMaterials.forEach((m, i) => {
        m.clippingPlanes = this.capClip[i]!;
        m.needsUpdate = true;
      });
    }
  }

  /** Where the ray leaves the cut-away region, or null if it never passes through it. */
  private exitDistance(ray: Ray): number | null {
    if (this.planeCount === 0) return null;
    let enter = 0;
    let exit = Infinity;
    for (let i = 0; i < this.planeCount; i++) {
      const plane = this.beyond[i]!;
      const s0 = plane.distanceToPoint(ray.origin);
      const rate = plane.normal.dot(ray.direction);
      if (rate === 0) {
        if (s0 <= 0) return null;
        continue;
      }
      const t = -s0 / rate;
      if (rate > 0) enter = Math.max(enter, t);
      else exit = Math.min(exit, t);
    }
    return exit > enter && Number.isFinite(exit) ? exit : null;
  }
}

/** The union of a part's meshes' world bounding spheres, into spheres.get(part). */
function partSphere(part: ScenePart, spheres: Map<ScenePart, Sphere>): void {
  const out = spheres.get(part);
  if (!out) return;
  let first = true;
  for (const mesh of part.meshes) {
    const bs = mesh.geometry.boundingSphere;
    if (!bs) continue;
    _sphere.copy(bs).applyMatrix4(mesh.matrixWorld);
    if (first) {
      out.copy(_sphere);
      first = false;
    } else {
      out.union(_sphere);
    }
  }
}

function visibleInScene(object: Object3D): boolean {
  for (let o: Object3D | null = object; o; o = o.parent) if (!o.visible) return false;
  return true;
}

/** Whether the hit triangle faces away from the ray (the ray is inside the part after the hit). */
function isBackFace(mesh: Mesh, face: { a: number; b: number; c: number }, ray: Ray): boolean {
  const geometry = mesh.geometry as BufferGeometry;
  const position = geometry.getAttribute("position");
  if (!position) return false;
  _a.fromBufferAttribute(position, face.a).applyMatrix4(mesh.matrixWorld);
  _b.fromBufferAttribute(position, face.b).applyMatrix4(mesh.matrixWorld);
  _c.fromBufferAttribute(position, face.c).applyMatrix4(mesh.matrixWorld);
  _triangle.set(_a, _b, _c).getNormal(_normal);
  return _normal.dot(ray.direction) > 0;
}
